<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell *ngIf="!isDialogMode; else dialogLocationForm">

    <app-page-hero
        eyebrow="Operations Setup"
        [title]="createEditLocation.id ? 'Update Location' : 'Create Location'"
        [subtitle]="createEditLocation.id ? 'Update the master location used by shipments and operations.' : 'Create a reusable source, destination, warehouse, office, or customer location.'">
        <div actions>
            <p-button label="Cancel" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (onClick)="cancel()"></p-button>
            <p-button [label]="createEditLocation.id ? 'Reset' : 'Clear'" icon="fa-solid fa-xmark" severity="secondary" [outlined]="true" (onClick)="clearForm()"></p-button>
            <p-button [label]="createEditLocation.id ? 'Save Changes' : 'Create'" [icon]="createEditLocation.id ? 'fa-solid fa-floppy-disk' : 'fa-solid fa-plus'"
                [loading]="locationCreationInProgress" [disabled]="!canManageLocation" (onClick)="createUpdateLocation()"></p-button>
        </div>
    </app-page-hero>

    <ng-container *ngTemplateOutlet="locationForm"></ng-container>
</app-page-shell>

<ng-template #dialogLocationForm>
    <div class="location-dialog-shell">
        <ng-container *ngTemplateOutlet="locationForm"></ng-container>
    </div>
</ng-template>

<ng-template #locationForm>
    <form class="location-form-stack" [formGroup]="createEditLocationForm" novalidate autocomplete="off" autocorrect="off"
        autocapitalize="off" spellcheck="off">
        <div class="p-fluid">
            <app-page-form-section
                icon="fa-solid fa-map-location-dot"
                title="Location Details"
                subtitle="Search the map source first, then confirm the location code and display name.">
                <div class="formgrid grid">
                    <div class="field col-12">
                        <p-inputGroup>
                            <p-inputGroupAddon>
                                <i class="fa-brands fa-google"></i>
                            </p-inputGroupAddon>
                            <p-iftaLabel>
                                <input id="googleMapsUrl" type="url" pInputText formControlName="googleMapsUrl"
                                    placeholder="https://maps.app.goo.gl/..." />
                                <label for="googleMapsUrl">Google Maps Link</label>
                            </p-iftaLabel>
                            <p-inputGroupAddon>
                                <p-button label="Extract" icon="pi pi-map-marker" severity="secondary"
                                    [loading]="isGoogleMapsUrlExtracting" [disabled]="!canManageLocation"
                                    (onClick)="extractGoogleMapsUrl()"></p-button>
                            </p-inputGroupAddon>
                        </p-inputGroup>
                        <small class="google-maps-url-hint">Paste a Google Maps share link to load its coordinates into Google Places search.</small>
                        @if (googleMapsUrlStatus) {
                        <small class="google-maps-url-status">{{ googleMapsUrlStatus }}</small>
                        }
                        @if (googleMapsUrlError) {
                        <small class="p-error">{{ googleMapsUrlError }}</small>
                        }
                    </div>
                    <div class="field col-12">
                        <p-inputGroup>
                            <p-inputGroupAddon>
                                <i class="fa-solid fa-map-location-dot"></i>
                            </p-inputGroupAddon>
                            <p-iftaLabel>
                                <app-location-search formControlName="searchLocation"
                                    (placeSelected)="placeSelect($event)" class="w-full"></app-location-search>
                                <label for="searchLocation">Search Google Places</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        @if (shouldShowFieldError('searchLocation')) {
                        <small class="p-error">Location search is required.</small>
                        }
                        @if (shouldShowLocationCoordinatesError()) {
                        <small class="p-error">{{ locationId ? 'Enter valid latitude and longitude below, or select a location from search results.' : 'Select a location from the search results.' }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6" *ngIf="locationId">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-location-crosshairs"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input id="locationLatitude" type="number" pInputText step="any" min="-90" max="90"
                                    [ngModel]="createEditLocation.location?.latitude"
                                    (ngModelChange)="setCoordinate('latitude', $event)"
                                    [ngModelOptions]="{ standalone: true }" [disabled]="!canManageLocation" />
                                <label for="locationLatitude">Latitude</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6" *ngIf="locationId">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-location-crosshairs"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input id="locationLongitude" type="number" pInputText step="any" min="-180" max="180"
                                    [ngModel]="createEditLocation.location?.longitude"
                                    (ngModelChange)="setCoordinate('longitude', $event)"
                                    [ngModelOptions]="{ standalone: true }" [disabled]="!canManageLocation" />
                                <label for="locationLongitude">Longitude</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12">
                        <p-inputGroup>
                            <p-inputGroupAddon>
                                <i class="fa-solid fa-cubes-stacked"></i>
                            </p-inputGroupAddon>
                            <p-iftaLabel>
                                <input id="locationName" type="text" pInputText
                                    [(ngModel)]="createEditLocation.name" formControlName="nameInput" />
                                <label for="locationName">Location Name</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        @if (shouldShowFieldError('nameInput')) {
                        <small class="p-error">Location name is required.</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon>
                                <i class="fa-solid fa-earth-americas"></i>
                            </p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select id="country" [options]="countryList" optionLabel="name" optionValue="id"
                                    [(ngModel)]="createEditLocation.countryId" formControlName="countryDropdown"
                                    [filter]="true" filterBy="name,countryCode" [showClear]="true"
                                    (onChange)="onCountrySelected($event.value)" appendTo="body" styleClass="w-full">
                                </p-select>
                                <label for="country">Country</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        @if (shouldShowFieldError('countryDropdown') || (hasSubmittedLocationForm && !createEditLocation.countryId)) {
                        <small class="p-error">Country is required.</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon>
                                <i class="fa-solid fa-city"></i>
                            </p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select id="city" [options]="filteredCityList" optionLabel="cityName" optionValue="id"
                                    [(ngModel)]="createEditLocation.cityId" formControlName="cityDropdown"
                                    [filter]="true" filterBy="cityName,searchText,country.name" [showClear]="true"
                                    (onChange)="onCitySelected($event.value)" appendTo="body" styleClass="w-full">
                                    <ng-template #selectedItem let-city>
                                        <span>{{ cityLabel(city) }}</span>
                                    </ng-template>
                                    <ng-template #item let-city>
                                        <div class="location-option">
                                            <span class="location-option-title">{{ cityLabel(city) }}</span>
                                            <small class="location-option-meta">{{ city.country?.name || 'No country' }}</small>
                                        </div>
                                    </ng-template>
                                </p-select>
                                <label for="city">City</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        @if (shouldShowFieldError('cityDropdown') || (hasSubmittedLocationForm && !createEditLocation.cityId)) {
                        <small class="p-error">City is required.</small>
                        }
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section
                icon="pi pi-sitemap"
                title="Association & Type"
                subtitle="Assign the owning company, operational type, and default contact.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon>
                                <i class="pi pi-building"></i>
                            </p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select id="company" [options]="companyList"
                                    [(ngModel)]="createEditLocation.company" optionLabel="name" [filter]="true"
                                    filterBy="name" [showClear]="true" formControlName="companyDropdown"
                                    (onChange)="onCompanySelected($event.value)" appendTo="body" styleClass="w-full">
                                </p-select>
                                <label for="company">Owning Company</label>
                            </p-iftaLabel>
                            <p-inputGroupAddon>
                                <p-button icon="pi pi-plus" severity="secondary" ariaLabel="Add parent company"
                                    (onClick)="registerCompany()"></p-button>
                            </p-inputGroupAddon>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon>
                                <i class="pi pi-building"></i>
                            </p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select id="retailer" [options]="retailerList"
                                    [(ngModel)]="createEditLocation.retailer" optionLabel="name" [filter]="true"
                                    filterBy="name" [showClear]="true" appendTo="body" styleClass="w-full">
                                </p-select>
                                <label for="retailer">Retailer / Store Chain</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon>
                                <i class="pi pi-th-large"></i>
                            </p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select id="location-type" [options]="locationTypes"
                                    [(ngModel)]="createEditLocation.locationType" optionLabel="name"
                                    optionValue="value" formControlName="locationTypeDropdown" appendTo="body"
                                    styleClass="w-full">
                                </p-select>
                                <label for="location-type">Location Type</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        @if (shouldShowFieldError('locationTypeDropdown')) {
                        <small class="p-error">Location type is required.</small>
                        }
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon>
                                <i class="pi pi-map"></i>
                            </p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select id="location-zone" [options]="locationZones"
                                    [(ngModel)]="createEditLocation.locationZoneId" optionLabel="name"
                                    optionValue="id" formControlName="locationZoneDropdown" [filter]="true"
                                    filterBy="name,code" [showClear]="true" appendTo="body" styleClass="w-full">
                                    <ng-template #item let-zone>
                                        <div class="location-option">
                                            <span class="location-option-title">{{ zone.name }}</span>
                                            <small class="location-option-meta">{{ zone.code }}</small>
                                        </div>
                                    </ng-template>
                                </p-select>
                                <label for="location-zone">Location Zone Optional</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon>
                                <i class="fa-solid fa-square-phone"></i>
                            </p-inputGroupAddon>
                            <p-iftaLabel>
                                <input id="defaultContact" type="text" pInputText formControlName="searchDefaultContact"
                                    readonly (click)="openContactSearchDialog()" />
                                <label for="defaultContact">Default Contact</label>
                            </p-iftaLabel>
                            <p-inputGroupAddon>
                                <p-button icon="pi pi-search" severity="secondary" ariaLabel="Search default contact"
                                    (onClick)="openContactSearchDialog()"></p-button>
                            </p-inputGroupAddon>
                            <p-inputGroupAddon *ngIf="createEditLocation.defaultContact">
                                <p-button icon="pi pi-times" severity="secondary" ariaLabel="Clear default contact"
                                    (onClick)="onContactCleared()"></p-button>
                            </p-inputGroupAddon>
                            <p-inputGroupAddon>
                                <p-button icon="pi pi-plus" severity="secondary" ariaLabel="Create default contact"
                                    (onClick)="registerContact()"></p-button>
                            </p-inputGroupAddon>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-8">
                        <p-inputGroup>
                            <p-inputGroupAddon>
                                <i class="pi pi-users"></i>
                            </p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-multiSelect id="location-contacts" [options]="customerList"
                                    [(ngModel)]="createEditLocation.contacts" optionLabel="name"
                                    formControlName="contactsDropdown" display="chip" [filter]="true"
                                    filterBy="name,emailAddress,phoneNumber" [showClear]="true" appendTo="body"
                                    styleClass="w-full">
                                    <ng-template pTemplate="selectedItems" let-selectedContacts>
                                        @if (selectedContacts?.length) {
                                        <span>{{ selectedContacts.length }} contact{{ selectedContacts.length === 1 ? '' : 's' }}</span>
                                        } @else {
                                        <span></span>
                                        }
                                    </ng-template>
                                    <ng-template #item let-contact>
                                        <div class="location-option">
                                            <span class="location-option-title">{{ contactLabel(contact) }}</span>
                                            <small class="location-option-meta">{{ contactMeta(contact) || 'No phone or email' }}</small>
                                        </div>
                                    </ng-template>
                                </p-multiSelect>
                                <label for="location-contacts">Contacts With Access</label>
                            </p-iftaLabel>
                            <p-inputGroupAddon>
                                <p-button icon="pi pi-search" severity="secondary" ariaLabel="Search contacts"
                                    (onClick)="openContactSearchDialog('access')"></p-button>
                            </p-inputGroupAddon>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12">
                        <div class="surface-50 border-1 border-200 border-round p-3">
                            <div class="flex justify-content-between align-items-center mb-3">
                                <div>
                                    <div class="font-medium">Merchandiser Account Mapping</div>
                                    <small class="text-600">Add one row per CRM client/account and merchandiser assignment.</small>
                                </div>
                                <p-button icon="pi pi-plus" label="Add Mapping" severity="secondary" [outlined]="true"
                                    [disabled]="!canManageLocation" (onClick)="addMerchandiserAssignment()"></p-button>
                            </div>

                            @if (assignmentRows().length) {
                            <div class="flex flex-column gap-3">
                                @for (assignment of assignmentRows(); track $index) {
                                <div class="grid align-items-end border-1 border-200 border-round p-2">
                                    <div class="field col-12 md:col-4 mb-0">
                                        <p-iftaLabel>
                                            <p-select [inputId]="'assignment-client-' + $index" [options]="crmClientList"
                                                [(ngModel)]="assignment.crmClientId" [ngModelOptions]="{ standalone: true }"
                                                optionLabel="companyName" optionValue="id" [filter]="true" [showClear]="true"
                                                appendTo="body" styleClass="w-full"
                                                (onChange)="onAssignmentClientChange(assignment)"></p-select>
                                            <label [for]="'assignment-client-' + $index">CRM Client</label>
                                        </p-iftaLabel>
                                    </div>
                                    <div class="field col-12 md:col-4 mb-0">
                                        <p-iftaLabel>
                                            <p-select [inputId]="'assignment-account-' + $index" [options]="accountsForClient(assignment.crmClientId)"
                                                [(ngModel)]="assignment.crmAccountId" [ngModelOptions]="{ standalone: true }"
                                                optionLabel="companyName" optionValue="id" [filter]="true" [showClear]="true"
                                                appendTo="body" styleClass="w-full"
                                                (onChange)="onAssignmentAccountChange(assignment)"></p-select>
                                            <label [for]="'assignment-account-' + $index">CRM Account</label>
                                        </p-iftaLabel>
                                    </div>
                                    <div class="field col-12 md:col-3 mb-0">
                                        <p-inputGroup>
                                            <p-iftaLabel>
                                                <p-select [inputId]="'assignment-merchandiser-' + $index" [options]="merchandiserList"
                                                    [(ngModel)]="assignment.merchandiserId" [ngModelOptions]="{ standalone: true }"
                                                    optionLabel="name" optionValue="id" [filter]="true"
                                                    filterBy="name,emailAddress,phoneNumber,mobileNumber,employeeCode,region"
                                                    [showClear]="true" appendTo="body" styleClass="w-full"
                                                    (onChange)="onAssignmentMerchandiserChange(assignment)"></p-select>
                                                <label [for]="'assignment-merchandiser-' + $index">Merchandiser</label>
                                            </p-iftaLabel>
                                            <p-inputGroupAddon>
                                                <p-button icon="pi pi-plus" severity="secondary" [text]="true"
                                                    [disabled]="!canManageLocation"
                                                    (onClick)="openMerchandiserDialog($index)"></p-button>
                                            </p-inputGroupAddon>
                                        </p-inputGroup>
                                    </div>
                                    <div class="field col-12 md:col-1 mb-0 flex justify-content-end">
                                        <p-button icon="pi pi-trash" severity="danger" [text]="true"
                                            [disabled]="!canManageLocation"
                                            (onClick)="removeMerchandiserAssignment($index)"></p-button>
                                    </div>
                                </div>
                                }
                            </div>
                            } @else {
                            <div class="text-600">No merchandiser mappings added.</div>
                            }
                        </div>
                    </div>
                </div>
            </app-page-form-section>

            <p-message *ngIf="errorMessages" severity="error" [text]="errorMessages"></p-message>

            <div class="location-dialog-actions" *ngIf="isDialogMode">
                <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="cancel()"></p-button>
                <p-button [label]="createEditLocation.id ? 'Save Changes' : 'Create'" [icon]="createEditLocation.id ? 'fa-solid fa-floppy-disk' : 'fa-solid fa-plus'"
                    [loading]="locationCreationInProgress" [disabled]="!canManageLocation" (onClick)="createUpdateLocation()"></p-button>
            </div>
        </div>
    </form>
</ng-template>

<p-dialog [header]="contactDialogMode === 'access' ? 'Search Location Contact' : 'Search Default Contact'" [(visible)]="showContactSearchDialog" [modal]="true"
    [style]="{ width: '48rem' }" [breakpoints]="{ '960px': '90vw', '640px': '96vw' }"
    [draggable]="false" [resizable]="false" styleClass="contact-search-dialog">
    <div class="contact-search-panel">
        <p-inputGroup>
            <p-inputGroupAddon>
                <i class="pi pi-search"></i>
            </p-inputGroupAddon>
            <input pInputText type="search" [(ngModel)]="contactSearchQuery"
                [ngModelOptions]="{ standalone: true }" placeholder="Search by name, email, or phone"
                (keydown.enter)="searchContacts()" />
            <p-inputGroupAddon>
                <p-button label="Search" icon="pi pi-search" [loading]="isContactSearchLoading"
                    (onClick)="searchContacts()"></p-button>
            </p-inputGroupAddon>
        </p-inputGroup>

        <div class="contact-result-list">
            @if (isContactSearchLoading) {
            <div class="contact-result-empty">Searching contacts...</div>
            } @else if (searchContactResults?.length) {
            @for (contact of searchContactResults; track contact.id) {
            <button type="button" class="contact-result-row" (click)="selectContactFromDialog(contact)">
                <span class="contact-result-name">{{ contactLabel(contact) }}</span>
                <span class="contact-result-meta">{{ contactMeta(contact) || 'No phone or email' }}</span>
                @if (contact.company?.name) {
                <span class="contact-result-company">{{ contact.company.name }}</span>
                }
            </button>
            }
            } @else {
            <div class="contact-result-empty">No contacts found.</div>
            }
        </div>
    </div>
    <ng-template pTemplate="footer">
        <div class="dialog-footer">
            <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true"
                (onClick)="showContactSearchDialog = false"></p-button>
            <p-button label="Create Contact" icon="pi pi-plus" (onClick)="registerDefaultContact()"></p-button>
        </div>
    </ng-template>
</p-dialog>

<app-customer-form [displayModal]="showCustomerForm" [dialogHeader]="customerFormHeader"
    [submitButtonLabel]="customerFormSubmitLabel" [company]="selectedCompany" [customer]="selectedCustomer"
    [companyRequired]="false" (modalClosed)="onCustomerFormClosed($event)">
</app-customer-form>

<app-company-form [displayModal]="showCompanyForm" [dialogHeader]="companyFormHeader"
    [submitButtonLabel]="companyFormSubmitLabel" [company]="selectedCompany" (modalClosed)="onCompanyFormClosed($event)">
</app-company-form>

<p-dialog header="Create Merchandiser" [(visible)]="showMerchandiserForm" [modal]="true"
    [style]="{ width: '56rem' }" [breakpoints]="{ '960px': '92vw', '640px': '98vw' }"
    [draggable]="false" [resizable]="false" [closable]="false">
    <app-crm-merchandiser-form
        [embedded]="true"
        [dialogCompanyId]="merchandiserDialogCompanyId()"
        [contextClientName]="activeMerchandiserClientName()"
        [contextAccountName]="activeMerchandiserAccountName()"
        (merchandiserSaved)="onMerchandiserSaved($event)"
        (formCancelled)="closeMerchandiserDialog()">
    </app-crm-merchandiser-form>
</p-dialog>
